/**
 * Thin adapter: generated `visitorThreads` query nodes → the widget's
 * semantic `ThreadListItem`. This is the single seam the UI hides behind, so a
 * backend field-name change (Conversation Model Phase 3 is still settling)
 * touches only this file, never the thread-list component.
 *
 * See `lib/widget-thread.ts` for why the UI is built against a local semantic
 * type rather than the generated one directly.
 */

import type { ChatStatus } from "@/lib/graphql/generated/types";
import type { VisitorThreadsQuery } from "@/lib/graphql/queries/generated/messaging.generated";
import type { ThreadUpdateEvent } from "@/lib/use-visitor-threads-realtime";
import type { ThreadListItem, ThreadPreview, ThreadStatus } from "@/lib/widget-thread";

/** One thread row as it comes back from the `VisitorThreads` query. */
export type VisitorThreadNode = VisitorThreadsQuery["visitorThreads"]["threads"][number];

/**
 * Collapse the BE `ChatStatus` enum (operator-facing, 5 states) down to the 4
 * the visitor's thread list cares about. `IN_PROGRESS`/`ISSUE` — an operator's
 * working states — both read to the visitor as an open, live conversation;
 * `null`/unknown defaults to OPEN so a thread never silently disappears.
 */
export function toThreadStatus(status: ChatStatus | null | undefined): ThreadStatus {
  switch (status) {
    case "PENDING":
      return "PENDING";
    case "RESOLVED":
      return "RESOLVED";
    case "CLOSED":
      return "CLOSED";
    default:
      return "OPEN";
  }
}

/**
 * Coerce a `DateTime` scalar (typed `unknown` by codegen — the BE may ship it
 * as an ISO string or an epoch number) to an ISO string the UI's
 * `Intl.RelativeTimeFormat` can parse. Unparseable → "" (the row degrades to
 * no timestamp rather than throwing).
 */
export function toIso(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || value instanceof Date) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString();
  }
  return "";
}

/**
 * Reduce a rich-text message body (the BE stores messages as HTML) to the
 * single line of plain text a thread-row preview shows. Strips tags, turns
 * block boundaries into spaces so `<p>a</p><p>b</p>` reads as "a b" rather
 * than "ab", decodes the handful of entities that survive stripping, and
 * collapses whitespace. Not a sanitizer — output is only ever rendered as
 * text, never as HTML.
 */
export function stripHtmlToText(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;|&apos;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/** Build the last-message preview from the flat trio, or `null` when the
 *  thread has no visitor-facing message yet. */
function toPreview(node: VisitorThreadNode): ThreadPreview | null {
  const text = node.lastMessagePreview ? stripHtmlToText(node.lastMessagePreview) : "";
  const mediaType = node.lastMessageMediaType ?? null;
  const thumb = node.lastMessageMediaThumbUrl ?? null;
  const at = toIso(node.lastMessageAt ?? node.updatedAt);
  const fromVisitor = !!node.lastMessageFromVisitor;
  // Caption present → show it, but still carry the media type + thumb so a
  // captioned image can render its thumbnail alongside.
  if (text) {
    return {
      text,
      at,
      fromVisitor,
      ...(mediaType ? { media: true, mediaType, mediaThumbUrl: thumb } : {}),
    };
  }
  // Typed media-only message → the row/peek renders a type icon + label (+ thumb
  // for visual media) instead of a blank line.
  if (mediaType) {
    return { text: "", at, fromVisitor, media: true, mediaType, mediaThumbUrl: thumb };
  }
  // Untyped fallback: empty text + a real (non-system) sender + a timestamp → a
  // media message the BE didn't type (unsupported kind → null mediaType). Show
  // the generic "Attachment". SYSTEM / internal notes (also blanked to "") carry
  // `system`/absent `lastMessageSenderKind`, so they stay dropped.
  const kind = node.lastMessageSenderKind;
  if (node.lastMessageAt && kind && kind !== "system") {
    return { text: "", at, fromVisitor, media: true };
  }
  return null;
}

/**
 * Map one generated thread node → `ThreadListItem`. `senderName` isn't a
 * visitor-safe field on `VisitorThread` (every thread is with the same team),
 * so the caller supplies it — normally the AI assistant's name.
 */
export function toThreadListItem(node: VisitorThreadNode, senderName: string): ThreadListItem {
  return {
    chatId: node.id,
    threadKey: node.threadKey ?? null,
    title: null,
    status: toThreadStatus(node.status),
    blocked: node.blocked ?? false,
    unreadCount: node.unReadCount ?? 0,
    updatedAt: toIso(node.updatedAt),
    preview: toPreview(node),
    senderName,
    lastSenderKind: node.lastMessageSenderKind ?? null,
    lastSenderName: node.lastMessageSenderName ?? null,
    lastMessageId: node.lastMessageId ?? null,
  };
}

/** Map a whole `VisitorThreads` query result → the thread list. Tolerates a
 *  missing/loading result (`undefined`) by returning an empty list. */
export function toThreadList(
  data: VisitorThreadsQuery | undefined,
  senderName: string,
): ThreadListItem[] {
  const threads = data?.visitorThreads?.threads;
  if (!threads) return [];
  return threads.map((node) => toThreadListItem(node, senderName));
}

/**
 * Accumulated live state for one thread, folded from the visitor-level
 * `threadUpdated` stream. Only the fields the stream has sent so far are set;
 * `undefined` means "no live value — keep the base query's value".
 */
export interface ThreadLive {
  status?: ThreadStatus;
  /** The message-slot thread title from a frame (`VisitorThread.title`). */
  title?: string | null;
  unreadCount?: number;
  updatedAt?: string;
  preview?: ThreadPreview | null;
  /** The preview came from a frame WITHOUT a time / direction (an edit of the last message) and no live
   *  value was held: `applyThreadLive` takes that part from the query row. */
  previewInheritsAt?: boolean;
  previewInheritsDirection?: boolean;
  typing?: boolean;
  assignedStatus?: "connected" | "pending" | null;
  assignedOperatorName?: string | null;
  /** Last-message sender from a live CONTENT message (keeps the row's sender
   *  correct without a reload). Set only when the event carried a message. */
  lastSenderKind?: string | null;
  lastSenderName?: string | null;
  /** Live per-thread block state (from a `blockScope: "THREAD"` event). A
   *  CONTACT-scope block is a GLOBAL flag handled by the shell, not here. */
  blocked?: boolean;
  /** When (ms, client clock) a frame last set a CONTENT field above (everything but typing /
   *  assignment). Lets a later query result that was requested after this moment win over it
   *  (see `pruneThreadLive`). Never read for display. */
  contentAt?: number;
}

/** The fields a content frame sets (vs the transient typing / assignment ones). */
const CONTENT_KEYS = [
  "status",
  "title",
  "unreadCount",
  "updatedAt",
  "preview",
  "lastSenderKind",
  "lastSenderName",
  "blocked",
] as const satisfies readonly (keyof ThreadLive)[];

const ms = (iso: string | null | undefined): number => (iso ? Date.parse(iso) : Number.NaN);

/** A frame older than what the thread already holds (a replay after a reconnect, an out-of-order
 *  delivery) must not overwrite the newer preview / unread. Compares the message time first, the
 *  row's `updatedAt` otherwise; a frame or a held value without a usable timestamp is never stale. */
function isStaleFrame(prev: ThreadLive | undefined, e: ThreadUpdateEvent): boolean {
  const msgAt = ms(e.lastMessageAt);
  const heldMsgAt = ms(prev?.preview?.at);
  if (!Number.isNaN(msgAt) && !Number.isNaN(heldMsgAt)) return msgAt < heldMsgAt;
  const updatedAt = ms(e.updatedAt);
  const heldUpdatedAt = ms(prev?.updatedAt);
  return !Number.isNaN(updatedAt) && !Number.isNaN(heldUpdatedAt) && updatedAt < heldUpdatedAt;
}

/** BE `ChatStatus`-ish string (uppercase) → local `ThreadStatus`. The stream
 *  sends the raw status string, same values as the enum. */
function statusFromString(status: string | null | undefined): ThreadStatus {
  switch (status) {
    case "PENDING":
      return "PENDING";
    case "RESOLVED":
      return "RESOLVED";
    case "CLOSED":
      return "CLOSED";
    default:
      return "OPEN";
  }
}

/**
 * Fold one `threadUpdated` event onto a thread's accumulated live state.
 * Persistent fields overwrite when present; the transient `typing`/`assignment`
 * fields are sent only when they change (and `null` clears them).
 */
export function reduceThreadLive(
  prev: ThreadLive | undefined,
  e: ThreadUpdateEvent,
  now: number = Date.now(),
): ThreadLive {
  const next: ThreadLive = { ...prev };
  const stale = isStaleFrame(prev, e);
  if (!stale) {
    foldContent(next, e, prev?.preview);
    if (CONTENT_KEYS.some((k) => next[k] !== prev?.[k])) next.contentAt = now;
  }
  if (e.typing !== undefined) next.typing = !!e.typing?.active;
  if (e.assignment !== undefined) {
    next.assignedStatus = e.assignment?.assignedStatus ?? null;
    next.assignedOperatorName = e.assignment?.operator?.name ?? null;
  }
  return next;
}

function setFlag(
  live: ThreadLive,
  key: "previewInheritsAt" | "previewInheritsDirection",
  on: boolean,
): void {
  if (on) live[key] = true;
  else delete live[key];
}

/** Fold the content part of a frame (everything a stale frame must not touch). */
function foldContent(next: ThreadLive, e: ThreadUpdateEvent, held?: ThreadLive["preview"]): void {
  if (e.status != null) next.status = statusFromString(e.status);
  if (e.title != null) next.title = e.title;
  if (e.unReadCount != null) next.unreadCount = e.unReadCount;
  if (e.updatedAt != null) next.updatedAt = toIso(e.updatedAt);
  // The last message was deleted: the held preview shows deleted text. Drop it so the row falls back to
  // the query row (the shell refetches it); the sort time stays the frame's `updatedAt`.
  if (e.lastMessageDeletedId) {
    delete next.preview;
    delete next.previewInheritsAt;
    delete next.previewInheritsDirection;
  }
  // A message arrival (a real preview OR just a fresh lastMessageAt) ends the
  // typing indicator — the BE doesn't always send a separate
  // `typing:{active:false}` (an explicit `typing` on THIS event still wins,
  // handled by the caller).
  if (e.lastMessagePreview != null || e.lastMessageAt != null) next.typing = false;
  // A content update arrives as a text preview OR a media-typed frame (a
  // media-only message carries a null `lastMessagePreview` but a
  // `lastMessageMediaType`), so react to either. Mirrors `toPreview`.
  if (e.lastMessagePreview != null || e.lastMessageMediaType != null) {
    const text = e.lastMessagePreview ? stripHtmlToText(e.lastMessagePreview) : "";
    const mediaType = e.lastMessageMediaType ?? null;
    const thumb = e.lastMessageMediaThumbUrl ?? null;
    // A frame with no time / direction (an edit of the last message) keeps the held row's: blanking them
    // would sink the thread in the recency sort and drop the "You:" prefix. With nothing held yet (a fresh
    // list) the query row supplies them, see `applyThreadLive`.
    const timeless = e.lastMessageAt == null && e.updatedAt == null;
    const directionless = e.lastMessageFromVisitor == null;
    const at = held && timeless ? held.at : toIso(e.lastMessageAt ?? e.updatedAt);
    const fromVisitor = held && directionless ? held.fromVisitor : !!e.lastMessageFromVisitor;
    setFlag(next, "previewInheritsAt", timeless && (held ? !!next.previewInheritsAt : true));
    setFlag(
      next,
      "previewInheritsDirection",
      directionless && (held ? !!next.previewInheritsDirection : true),
    );
    if (text) {
      // Text / caption present → show it (+ carry media type/thumb for a caption).
      next.preview = {
        text,
        at,
        fromVisitor,
        ...(mediaType ? { media: true, mediaType, mediaThumbUrl: thumb } : {}),
      };
    } else if (mediaType) {
      // Typed media-only → type icon + label (+ thumb), not a blank line.
      next.preview = { text: "", at, fromVisitor, media: true, mediaType, mediaThumbUrl: thumb };
    } else if (
      e.lastMessageAt != null &&
      e.lastMessageSenderKind &&
      e.lastMessageSenderKind !== "system"
    ) {
      // Untyped fallback: empty text from a real sender → generic media.
      next.preview = { text: "", at, fromVisitor, media: true };
    }
    // else: a blanked SYSTEM / internal note — leave the prior preview intact.
  }
  // A content message carries the sender discriminator (typing/assignment/read
  // events send null → keep the last known sender). Update the row's sender so
  // a live operator/AI reply doesn't read as the stale loaded sender.
  if (e.lastMessageSenderKind != null) {
    next.lastSenderKind = e.lastMessageSenderKind;
    next.lastSenderName = e.lastMessageSenderName ?? null;
  }
  // Block toggle for THIS thread. Fold BOTH scopes: a THREAD event blocks just
  // this thread, and a CONTACT (global) event still names this thread's id — so
  // folding it clears/sets the row's own `blocked` too. Without this a CONTACT
  // UNBLOCK left the row's query-sourced `blocked` (which is "thread OR global")
  // stale-true, keeping the composer locked until reload. The GLOBAL flag is
  // tracked separately by the shell (drives new-conversation + other threads).
  if (e.blocked != null) next.blocked = e.blocked;
}

/**
 * Drop the content a refetch has superseded: every live entry whose content arrived BEFORE the
 * request that just completed (`startedAt`, client clock) is stripped back to its transient parts
 * (typing, assignment); the query result is the truth for the rest. Content that arrived after the
 * request started is kept, since the response may predate it. Returns the same object when
 * nothing needed pruning.
 */
export function pruneThreadLive(
  live: Record<string, ThreadLive>,
  startedAt: number,
): Record<string, ThreadLive> {
  let changed = false;
  const out: Record<string, ThreadLive> = {};
  for (const [id, entry] of Object.entries(live)) {
    if (entry.contentAt !== undefined && entry.contentAt > startedAt) {
      out[id] = entry;
      continue;
    }
    const hasContent = CONTENT_KEYS.some((k) => entry[k] !== undefined);
    if (!hasContent) {
      out[id] = entry;
      continue;
    }
    changed = true;
    const rest: ThreadLive = { ...entry };
    for (const k of CONTENT_KEYS) delete rest[k];
    delete rest.contentAt;
    delete rest.previewInheritsAt;
    delete rest.previewInheritsDirection;
    if (Object.values(rest).some((v) => v !== undefined)) out[id] = rest;
  }
  return changed ? out : live;
}

/** The live preview, with the time / direction an edit frame did not carry taken from the query row. */
function inheritFromBase(
  live: ThreadLive,
  base: ThreadPreview | null | undefined,
): ThreadPreview | null | undefined {
  const p = live.preview;
  if (!p || !base) return p;
  return {
    ...p,
    ...(live.previewInheritsAt ? { at: base.at } : {}),
    ...(live.previewInheritsDirection ? { fromVisitor: base.fromVisitor } : {}),
  };
}

/** Merge a thread's live state onto its base (query) row. Returns the base row
 *  unchanged when there's no live state. */
export function applyThreadLive(
  item: ThreadListItem,
  live: ThreadLive | undefined,
): ThreadListItem {
  if (!live) return item;
  // A live preview older than the loaded row's means the query already has newer truth (a refetch
  // landed after a missed window): its content must not mask it.
  const liveAt = ms(live.preview?.at);
  const baseAt = ms(item.preview?.at);
  const liveIsOlder = !Number.isNaN(liveAt) && !Number.isNaN(baseAt) && liveAt < baseAt;
  const c = liveIsOlder ? undefined : live;
  return {
    ...item,
    ...(c?.status !== undefined ? { status: c.status } : {}),
    ...(c?.title !== undefined ? { title: c.title } : {}),
    ...(c?.unreadCount !== undefined ? { unreadCount: c.unreadCount } : {}),
    ...(c?.updatedAt !== undefined ? { updatedAt: c.updatedAt } : {}),
    ...(c?.preview !== undefined ? { preview: inheritFromBase(c, item.preview) } : {}),
    ...(live.typing !== undefined ? { typing: live.typing } : {}),
    ...(live.assignedStatus !== undefined ? { assignedStatus: live.assignedStatus } : {}),
    ...(live.assignedOperatorName !== undefined
      ? { assignedOperatorName: live.assignedOperatorName }
      : {}),
    ...(c?.lastSenderKind !== undefined ? { lastSenderKind: c.lastSenderKind } : {}),
    ...(c?.lastSenderName !== undefined ? { lastSenderName: c.lastSenderName } : {}),
    ...(c?.blocked !== undefined ? { blocked: c.blocked } : {}),
  };
}
